import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, User as UserIcon, LogOut, LayoutDashboard } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const link = ({ isActive }) =>
    `text-sm transition-colors ${isActive ? "text-white" : "text-white/60 hover:text-white"}`;

  return (
    <header className="sticky top-0 z-50 cw-glass">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="h-8 w-8 rounded-md bg-[#FF6B00] flex items-center justify-center font-display font-bold">C</div>
          <span className="font-display text-lg tracking-tight">CreativeWayz</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/marketplace" className={link} data-testid="nav-marketplace">Marketplace</NavLink>
          <NavLink to="/writers" className={link} data-testid="nav-writers">Become a Writer</NavLink>
          <a href="/#how" className="text-sm text-white/60 hover:text-white">How it works</a>
          <a href="/#faq" className="text-sm text-white/60 hover:text-white">FAQ</a>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="cw-btn-ghost flex items-center gap-2" data-testid="nav-user-menu">
                  <div className="h-6 w-6 rounded-full bg-[#FF6B00]/20 border border-[#FF6B00]/40 flex items-center justify-center text-xs">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="text-sm">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0F0F0F] border-white/10 text-white w-56">
                <DropdownMenuLabel className="text-white/60 text-xs">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="nav-dashboard">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="text-sm text-white/70 hover:text-white" data-testid="nav-login">Log in</Link>
              <Link to="/signup" className="cw-btn-primary text-sm" data-testid="nav-signup">Get started</Link>
            </>
          )}
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0A0A0A]/95">
          <div className="px-6 py-4 flex flex-col gap-4">
            <Link to="/marketplace" onClick={() => setOpen(false)}>Marketplace</Link>
            <Link to="/writers" onClick={() => setOpen(false)}>Become a Writer</Link>
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)}>Dashboard</Link>
                <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="text-left">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)}>Log in</Link>
                <Link to="/signup" onClick={() => setOpen(false)} className="cw-btn-primary text-sm inline-block w-fit">Get started</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
